/**
 * Embed provider detection for course links.
 * Known providers get rewritten to their iframe-embeddable URL form;
 * everything else is treated as an external link (most sites block framing
 * with X-Frame-Options/CSP, so we never promise embedding for arbitrary URLs).
 */

export interface EmbedInfo {
  /** iframe-ready URL, present only when the provider supports embedding */
  embedUrl?: string;
  /** Human name of the recognized provider, or the bare domain */
  provider: string;
  /** True when this is a YouTube video that belongs in Recommended Videos */
  isYouTube: boolean;
}

const domainOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

export const analyzeLink = (rawUrl: string): EmbedInfo => {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return { provider: '', isYouTube: false };
  }
  if (url.protocol !== 'https:') return { provider: domainOf(rawUrl), isYouTube: false };
  const host = url.hostname.replace(/^www\./, '');
  const path = url.pathname;

  // YouTube: belongs in Recommended Videos (transcripts + quiz sources)
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtu.be') {
    const videoId = host === 'youtu.be' ? path.slice(1).split('/')[0] : url.searchParams.get('v') || '';
    return {
      provider: 'YouTube',
      isYouTube: true,
      embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : undefined,
    };
  }

  // Vimeo
  if (host === 'vimeo.com') {
    const id = path.split('/').filter(Boolean)[0];
    if (id && /^\d+$/.test(id)) {
      return { provider: 'Vimeo', isYouTube: false, embedUrl: `https://player.vimeo.com/video/${id}` };
    }
  }

  // Loom
  if (host === 'loom.com' && path.startsWith('/share/')) {
    return { provider: 'Loom', isYouTube: false, embedUrl: `https://www.loom.com/embed/${path.slice('/share/'.length)}` };
  }

  // Google Docs / Slides / Sheets: swap trailing /edit for /preview
  if (host === 'docs.google.com' && /\/(document|presentation|spreadsheets)\/d\//.test(path)) {
    const preview = rawUrl.replace(/\/(edit|view)(\?.*)?$/, '/preview');
    return { provider: 'Google Docs', isYouTube: false, embedUrl: preview.includes('/preview') ? preview : `${rawUrl.replace(/\/$/, '')}/preview` };
  }

  // Google Drive file preview
  if (host === 'drive.google.com' && path.startsWith('/file/d/')) {
    const id = path.split('/')[3];
    return { provider: 'Google Drive', isYouTube: false, embedUrl: `https://drive.google.com/file/d/${id}/preview` };
  }

  // Figma
  if (host === 'figma.com' && (path.startsWith('/file/') || path.startsWith('/design/') || path.startsWith('/proto/'))) {
    return { provider: 'Figma', isYouTube: false, embedUrl: `https://embed.figma.com/embed?embed_host=classrooms&url=${encodeURIComponent(rawUrl)}` };
  }

  // Miro
  if (host === 'miro.com' && path.includes('/board/')) {
    return { provider: 'Miro', isYouTube: false, embedUrl: rawUrl.replace('/board/', '/live-embed/') };
  }

  // Canva published designs embed as-is with ?embed
  if (host === 'canva.com' && path.includes('/design/')) {
    return { provider: 'Canva', isYouTube: false, embedUrl: `${rawUrl.split('?')[0]}?embed` };
  }

  // Desmos, GeoGebra, PhET, CodePen embed their normal URLs
  if (host === 'desmos.com') return { provider: 'Desmos', isYouTube: false, embedUrl: rawUrl };
  if (host === 'geogebra.org') {
    const materialMatch = path.match(/\/m\/(\w+)/);
    return { provider: 'GeoGebra', isYouTube: false, embedUrl: materialMatch ? `https://www.geogebra.org/material/iframe/id/${materialMatch[1]}` : rawUrl };
  }
  if (host === 'phet.colorado.edu') {
    return { provider: 'PhET', isYouTube: false, embedUrl: path.includes('/sims/') ? rawUrl : undefined };
  }
  if (host === 'codepen.io' && path.includes('/pen/')) {
    return { provider: 'CodePen', isYouTube: false, embedUrl: rawUrl.replace('/pen/', '/embed/') };
  }

  // Genially interactive content: view URLs embed directly
  if (host === 'view.genially.com' || host === 'view.genial.ly') {
    return { provider: 'Genially', isYouTube: false, embedUrl: rawUrl };
  }
  if (host === 'genially.com' || host === 'genial.ly') {
    return { provider: 'Genially', isYouTube: false };
  }

  // Unknown provider: external link only
  return { provider: domainOf(rawUrl), isYouTube: false };
};
